import { registerStyles } from "../utils/registerStyles";
import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { calculateProjectRevenue } from "../utils/projectRevenue";
import ProjectEconomy from "./ProjectEconomy";
import { buildPayrollTrend } from "../utils/payrollTrend";
import { calculateDailyPersonnelCost, calculateMonthlyPersonnelCost } from "../utils/dailyPersonnelCost";

const localDateKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

// Reuse both pending and completed requests until the page is reloaded.
const payrollRequests = new Map();
const payrollResults = new Map();
const initialDate = localDateKey(new Date());
const initialPeriods = new Set([initialDate.slice(0, 4), initialDate.slice(0, 7), initialDate]);

function payrollCacheKey(companyUid, period) {
  return JSON.stringify([companyUid, initialPeriods.has(String(period)) ? "initial" : String(period)]);
}

function loadPayrollData(companyUid, period) {
  const key = payrollCacheKey(companyUid, period);
  if (!payrollRequests.has(key)) {
    const request = new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => reject(new Error("Löneunderlaget tog för lång tid att hämta.")), 30000);
      const collections = ["employed", "shifts", "bookings", "obRules", "holidays", "overtimeRules", "overtimeRequests", "absenceTypes", "absenceRequests", "customObEntries"];
      Promise.all([
        Promise.all(collections.map((name) => getDocs(collection(db, "users", companyUid, name)))),
        getDoc(doc(db, "users", companyUid)),
      ]).then(([snapshots, owner]) => {
        const [employees, shifts, bookings] = snapshots;
        const employeeRows = employees.docs.map((entry) => ({ ...entry.data(), id: entry.id }));
        if (owner.exists() && !employeeRows.some((employee) => employee.id === companyUid)) {
          employeeRows.push({ ...owner.data(), id: companyUid });
        }
        resolve({
          ...Object.fromEntries(collections.slice(3).map((name, index) => [name, snapshots[index + 3].docs.map((entry) => ({ ...entry.data(), id: entry.id }))])),
          employees: employeeRows,
          shifts: shifts.docs.map((entry) => ({ ...entry.data(), id: entry.id })),
          bookings: bookings.docs.map((entry) => entry.data()),
        });
      }).catch(reject).finally(() => window.clearTimeout(timeout));
    });
    payrollRequests.set(key, request.then((result) => {
      payrollResults.set(key, result);
      return result;
    }));
  }
  return payrollRequests.get(key);
}

function usePayrollData(companyUid, period) {
  const [data, setData] = useState({ employees: [], shifts: [], bookings: [] });
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const cached = payrollResults.get(payrollCacheKey(companyUid, period));
    if (cached) {
      setData(cached);
      setLoading(false);
      setError(false);
      return undefined;
    }
    setData({ employees: [], shifts: [], bookings: [] });
    setLoading(true);
    setError(false);

    if (!companyUid) {
      setError(true);
      setLoading(false);
      return undefined;
    }

    loadPayrollData(companyUid, period).then((result) => {
      if (active) setData(result);
    }).catch((fetchError) => {
      console.error("Kunde inte hämta löneunderlag:", fetchError);
      if (active) setError(true);
    }).finally(() => {
      if (active) setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [companyUid, period]);

  return { data, error, loading };
}

export default function PayrollTrendChart({ companyUid, companyName, projects = [], customersAndProjectsEnabled = true }) {
  const [selectedDate, setSelectedDate] = useState(() => localDateKey(new Date()));
  const [selectedMonth, setSelectedMonth] = useState(() => localDateKey(new Date()).slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const { data, loading, error } = usePayrollData(companyUid, selectedYear);
  const month = usePayrollData(companyUid, selectedMonth);
  const day = usePayrollData(companyUid, selectedDate);

  const trend = useMemo(() => buildPayrollTrend(data.employees, data.shifts, data.bookings, new Date(selectedYear, 0, 1)), [data, selectedYear]);
  const yearPoints = useMemo(() => trend.months.filter((point) => Number(point.period.slice(0, 4)) === selectedYear), [trend, selectedYear]);
  const dailyCost = useMemo(() => calculateDailyPersonnelCost(day.data, selectedDate), [day.data, selectedDate]);
  const monthlyCost = useMemo(() => calculateMonthlyPersonnelCost(month.data, selectedMonth), [month.data, selectedMonth]);
  const monthlyRevenue = useMemo(() => customersAndProjectsEnabled ? calculateProjectRevenue(month.data, projects, selectedMonth) : null, [month.data, projects, selectedMonth, customersAndProjectsEnabled]);
  const monthlyCount = useMemo(() => {
    const selectedTrend = buildPayrollTrend(month.data.employees, month.data.shifts, month.data.bookings, new Date(`${selectedMonth}-01T12:00:00`));
    return selectedTrend.months.find((point) => point.period === selectedMonth)?.count ?? 0;
  }, [month.data, selectedMonth]);
  const dailyCount = useMemo(() => buildPayrollTrend(day.data.employees, day.data.shifts, day.data.bookings, new Date(`${selectedDate}T12:00:00`))
    .daysForMonth(selectedDate.slice(0, 7)).find((point) => point.period === selectedDate)?.count ?? 0, [day.data, selectedDate]);
  const isCurrentMonth = selectedMonth === localDateKey(new Date()).slice(0, 7);
  const moveMonth = (offset) => {
    const date = new Date(`${selectedMonth}-01T12:00:00`);
    date.setMonth(date.getMonth() + offset);
    setSelectedMonth(localDateKey(date).slice(0, 7));
  };
  const isToday = selectedDate === localDateKey(new Date());
  const moveDay = (offset) => {
    const date = new Date(`${selectedDate}T12:00:00`);
    date.setDate(date.getDate() + offset);
    setSelectedDate(localDateKey(date));
  };
  const monthSelector = (
    <label className="economy-month-filter">Månad<input type="month" aria-label="Månad för ekonomisk översikt" value={selectedMonth} onChange={event => { if (/^\d{4}-(0[1-9]|1[0-2])$/.test(event.target.value)) setSelectedMonth(event.target.value); }} /></label>
  );
  return (
    <div className="economy-page">
      {customersAndProjectsEnabled && <section className="economy-overview" aria-label="Ekonomisk översikt för vald månad">
        <div className="economy-section-heading">
          {customersAndProjectsEnabled && <div><h2>Månadens översikt</h2><p>Kundfordran, personalkostnad och resultat samlat för vald månad.</p></div>}
          {monthSelector}
        </div>
        {customersAndProjectsEnabled && <>
        <div className="economy-summary">
          {[
            { label: "Kundfordran", value: monthlyRevenue.revenue, note: "Projektens timpris × bemannade timmar", tone: "income" },
            { label: "Personalkostnad", value: monthlyCost.gross, note: "Bruttolön inklusive tillägg", tone: "cost" },
            { label: "Resultat", value: monthlyRevenue.revenue - monthlyCost.gross, note: "Kundfordran minus personalkostnad", tone: monthlyRevenue.revenue - monthlyCost.gross < 0 ? "negative" : "positive" },
          ].map(item => <div key={item.label} className={`economy-summary-card economy-summary-card--${item.tone}`}>
            <span>{item.label}</span>
            <strong>{month.loading ? "Laddar…" : month.error ? "Ej tillgängligt" : item.value.toLocaleString("sv-SE", { style: "currency", currency: "SEK" })}</strong>
            <small>{item.note}</small>
          </div>)}
        </div>
        {!month.loading && !month.error && (monthlyRevenue.missingPriceHours > 0 || monthlyCost.missingSalaryCount > 0) && <p className="economy-incomplete">Underlag saknas för vissa timmar eller löner. Månadens belopp är ofullständiga; se detaljerna nedan.</p>}
        <p className="payroll-trend__day-note">Beräknade belopp inklusive planerade pass. Resultatet är före arbetsgivaravgifter och övriga företagskostnader.</p>
        </>}
      </section>}
      <section aria-label="Personalkostnad och löneunderlag">
        <div className="economy-section-heading"><div><h2>Personalkostnad</h2><p>Följ löneunderlaget över året och kostnaden för vald månad och dag.</p></div>{!customersAndProjectsEnabled && monthSelector}</div>
    <div className="payroll-trend-list">
      <PayrollChart data={data} points={yearPoints} selectedYear={selectedYear} onYearChange={setSelectedYear} companyName={companyName} loading={loading} error={error} />
      <section className="payroll-trend payroll-trend--day" aria-label="Personalkostnad för vald månad">
        <div className="payroll-trend__header"><h2>PERSONALKOSTNAD – MÅNAD</h2></div>
        <div className="payroll-trend__body">
          <div className="payroll-trend__day-navigation">
            <button type="button" onClick={() => moveMonth(-1)} aria-label="Föregående månad"><ChevronLeft size={18} /></button>
            <input
              type="month"
              aria-label="Välj månad"
              value={selectedMonth}
              onChange={(event) => {
                const value = event.target.value;
                if (/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) setSelectedMonth(value);
              }}
            />
            <button type="button" onClick={() => moveMonth(1)} aria-label="Nästa månad"><ChevronRight size={18} /></button>
          </div>
          <div className="payroll-trend__day-result" aria-live="polite">
            {month.loading ? <p>Laddar löner…</p> : month.error ? <p role="alert">Kunde inte hämta löner. Kontrollera anslutningen och ladda om sidan.</p> : (
              <><strong>{monthlyCost.gross.toLocaleString("sv-SE", { style: "currency", currency: "SEK" })}</strong><span>Bruttolön inklusive tillägg</span><span>Antal löner: {monthlyCount}</span></>
            )}
          </div>
          <p className="payroll-trend__day-note">Hela månadens kostnad inklusive OB, övertid och semesterersättning, med frånvaroavdrag. Även planerade pass ingår. Fast månadslön räknas en gång.</p>
          {!month.loading && !month.error && monthlyCost.missingSalaryCount > 0 && <p className="payroll-trend__day-note" role="status">Löneuppgifter saknas för {monthlyCost.missingSalaryCount} anställda med underlag denna månad. Deras lön ingår inte i beloppet.</p>}
          {!isCurrentMonth && <button className="payroll-trend__today" type="button" onClick={() => setSelectedMonth(localDateKey(new Date()).slice(0, 7))}>Till denna månad</button>}
        </div>
      </section>
      <section className="payroll-trend payroll-trend--day" aria-label="Personalkostnad för vald dag">
        <div className="payroll-trend__header"><h2>PERSONALKOSTNAD – {isToday ? "IDAG" : "VALD DAG"}</h2></div>
        <div className="payroll-trend__body">
          <div className="payroll-trend__day-navigation">
            <button type="button" onClick={() => moveDay(-1)} aria-label="Föregående dag"><ChevronLeft size={18} /></button>
            <input
              type="date"
              aria-label="Välj dag"
              value={selectedDate}
              onChange={(event) => {
                const value = event.target.value;
                if (/^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(new Date(`${value}T12:00:00`).getTime())) setSelectedDate(value);
              }}
            />
            <button type="button" onClick={() => moveDay(1)} aria-label="Nästa dag"><ChevronRight size={18} /></button>
          </div>
          <div className="payroll-trend__day-result" aria-live="polite">
            {day.loading ? <p>Laddar löner…</p> : day.error ? <p role="alert">Kunde inte hämta löner. Kontrollera anslutningen och ladda om sidan.</p> : (
              <><strong>{dailyCost.gross.toLocaleString("sv-SE", { style: "currency", currency: "SEK" })}</strong><span>Bruttolön inklusive tillägg</span><span>Antal löner: {dailyCount}</span></>
            )}
          </div>
          <p className="payroll-trend__day-note">Inklusive OB, övertid och semesterersättning, med frånvaroavdrag. Månadslön fördelas på månadens kalenderdagar. Pass räknas på startdagen.</p>
          {!day.loading && !day.error && dailyCost.missingSalaryCount > 0 && <p className="payroll-trend__day-note" role="status">Löneuppgifter saknas för {dailyCost.missingSalaryCount} anställda med underlag denna dag. Deras lön ingår inte i beloppet.</p>}
          {!isToday && <button className="payroll-trend__today" type="button" onClick={() => setSelectedDate(localDateKey(new Date()))}>Till idag</button>}
        </div>
      </section>
    </div>
    </section>
    {customersAndProjectsEnabled && <ProjectEconomy projects={projects} year={{ data, loading, error }} month={month} day={day}
      selectedYear={selectedYear} selectedMonth={selectedMonth} selectedDate={selectedDate}
      onYearChange={setSelectedYear} onMonthChange={setSelectedMonth} onDateChange={setSelectedDate} />}
    </div>
  );
}

function PayrollChart({ data, points, selectedYear, onYearChange, companyName, loading, error }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  useEffect(() => setHoveredIndex(null), [points]);
  const width = Math.max(580, points.length * 45);
  const height = 230;
  const left = 64;
  const right = 24;
  const top = 18;
  const bottom = 70;
  const plotHeight = height - top - bottom;
  const maxCount = Math.max(40, ...points.map((point) => point.count));
  const tickSize = maxCount <= 40 ? 10 : Math.ceil(maxCount / 40) * 10;
  const ceiling = Math.ceil(maxCount / tickSize) * tickSize;
  const x = (index) => left + (points.length === 1 ? (width - left - right) / 2 : index * (width - left - right) / (points.length - 1));
  const y = (count) => top + plotHeight * (1 - count / ceiling);
  const line = points.map((point, index) => `${index === 0 ? "M" : "L"} ${x(index)} ${y(point.count)}`).join(" ");
  const labelStep = Math.max(1, Math.ceil(points.length / 18));
  const hoveredPoint = hoveredIndex === null ? null : points[hoveredIndex];
  const hoveredCost = useMemo(() => hoveredPoint
    ? calculateMonthlyPersonnelCost(data, hoveredPoint.period).gross
    : null, [data, hoveredPoint]);
  const tooltipWidth = 300;
  const tooltipX = hoveredPoint ? Math.max(4, Math.min(width - tooltipWidth - 4, x(hoveredIndex) - tooltipWidth / 2)) : 0;
  const tooltipY = hoveredPoint ? (y(hoveredPoint.count) > 85 ? y(hoveredPoint.count) - 78 : y(hoveredPoint.count) + 12) : 0;
  const hoveredDate = hoveredPoint ? new Intl.DateTimeFormat("sv-SE", { month: "long", year: "numeric" })
    .format(new Date(Number(hoveredPoint.period.slice(0, 4)), Number(hoveredPoint.period.slice(5, 7)) - 1, 1)) : "";

  return (
    <section className="payroll-trend" aria-label="Löner över året, uppdelat per månad">
      <div className="payroll-trend__header">
        <h2>LÖNER ÖVER TID – PER ÅR</h2>
        <div className="payroll-trend__day-navigation payroll-trend__year-navigation">
          <button type="button" onClick={() => onYearChange(selectedYear - 1)} aria-label="Föregående år"><ChevronLeft size={18} /></button>
          <strong aria-live="polite">{selectedYear}</strong>
          <button type="button" onClick={() => onYearChange(selectedYear + 1)} aria-label="Nästa år"><ChevronRight size={18} /></button>
        </div>
      </div>
      <div className="payroll-trend__body">
        {loading ? <p className="payroll-trend__status">Laddar löner…</p> : error ? (
          <p className="payroll-trend__status" role="alert">Kunde inte hämta löner. Kontrollera anslutningen och ladda om sidan.</p>
        ) : (
          <>
            <div className="payroll-trend__legend"><span className="payroll-trend__legend-mark" />{companyName}</div>
            <div className="payroll-trend__scroll">
              <svg viewBox={`0 0 ${width} ${height}`} style={{ display: "block", width: "100%", minWidth: width, height: "auto" }} role="img" aria-label={`Antal löner per månad för ${companyName}, ${selectedYear}`}>
                <text className="payroll-trend__axis-title" transform={`translate(18 ${top + plotHeight / 2}) rotate(-90)`} textAnchor="middle">Antal löner</text>
                {Array.from({ length: ceiling / tickSize + 1 }, (_, index) => index * tickSize).map((tick) => (
                  <g key={tick}>
                    <line className="payroll-trend__grid" x1={left} x2={width - right} y1={y(tick)} y2={y(tick)} />
                    <text className="payroll-trend__tick" x={left - 18} y={y(tick) + 5} textAnchor="end">{tick}</text>
                  </g>
                ))}
                <path d={line} className="payroll-trend__line" />
                {points.map((point, index) => (
                  <g key={point.period}>
                    <circle
                      className={`payroll-trend__point${hoveredIndex === index ? " payroll-trend__point--hovered" : ""}`}
                      cx={x(index)} cy={y(point.count)} r={hoveredIndex === index ? 7 : 5}
                    />
                    <circle
                      className="payroll-trend__point-hit"
                      cx={x(index)} cy={y(point.count)} r="14"
                      aria-label={`${point.period}: ${point.count} löner`}
                      tabIndex={0}
                      onFocus={() => setHoveredIndex(index)}
                      onBlur={() => setHoveredIndex(null)}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    />
                    {index % labelStep === 0 && (
                      <text className="payroll-trend__tick" transform={`translate(${x(index) + 3} ${height - bottom + 19}) rotate(-48)`} textAnchor="end">{point.period}</text>
                    )}
                  </g>
                ))}
                {hoveredPoint && (
                  <g className="payroll-trend__tooltip" transform={`translate(${tooltipX} ${tooltipY})`} pointerEvents="none">
                    <rect width={tooltipWidth} height="70" rx="7" />
                    <text x="10" y="19">Datum: {hoveredDate}</text>
                    <text x="10" y="38">Totalt löner: {hoveredPoint.count}</text>
                    <text x="10" y="57">Personalkostnad – månad: {hoveredCost.toLocaleString("sv-SE", { style: "currency", currency: "SEK" })}</text>
                  </g>
                )}
              </svg>
            </div>
          </>
        )}
        <p className="payroll-trend__description">Antal anställda med löneunderlag per månad för {companyName}, oavsett godkännande. Varje anställd räknas en gång per period. Schemalagda pass ingår; sparad månadslön räknas även utan pass. Underlaget använder nuvarande löneinställningar.</p>
      </div>
    </section>
  );
}

// Styles live with this component; keep selectors and responsive rules together.
registerStyles("PayrollTrendChart", `
.economy-page { display: grid; gap: 32px; min-width: 0; padding: 8px 0 24px; }
.economy-page > *, .economy-page section { min-width: 0; }
.economy-section-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 12px; }
.economy-section-heading h2 { color: #111827; font-size: 20px; margin: 0; font-weight: 750; }
.economy-section-heading p { color: #6b7280; font-size: 14px; margin: 6px 0 0; line-height: 1.5; }
.economy-month-filter { margin-left: auto; display: flex; align-items: center; gap: 10px; font-size: 13px; color: #475569; font-weight: 600; }
.economy-month-filter input { padding: 9px 12px; border: 1px solid #d9dde4; border-radius: 8px; background: #fff; color: #111827; font: inherit; }
.economy-month-filter input:focus-visible { outline: 2px solid #3278bc; outline-offset: 2px; }
.economy-period { background: #eef2ff; color: #4338ca; border-radius: 8px; padding: 8px 12px; font-size: 14px; font-weight: 600; text-transform: capitalize; }
.economy-summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
.economy-summary-card { display: flex; flex-direction: column; gap: 10px; padding: 22px; background: #fff; border: 1px solid #e5e7eb; border-top: 3px solid #3278bc; border-radius: 12px; }
.economy-summary-card > span { color: #475569; font-size: 14px; font-weight: 600; }
.economy-summary-card strong { font-size: clamp(22px, 2.3vw, 34px); font-variant-numeric: tabular-nums; color: #1e40af; overflow-wrap: anywhere; }
.economy-summary-card small { color: #6b7280; font-size: 12px; line-height: 1.5; }
.economy-summary-card--income { border-top-color: #b45309; }
.economy-summary-card--income strong { color: #b45309; }
.economy-summary-card--cost { border-top-color: #68a4df; }
.economy-summary-card--cost strong { color: #3278bc; }
.economy-summary-card--positive { border-top-color: #15803d; background: #f7fdf9; }
.economy-summary-card--positive strong { color: #15803d; }
.economy-summary-card--negative { border-top-color: #b91c1c; background: #fffafa; }
.economy-summary-card--negative strong { color: #b91c1c; }
.economy-incomplete { padding: 10px 14px; border-radius: 8px; background: #fffbeb; color: #92400e; font-size: 13px; line-height: 1.5; }
.economy-period-totals { display: grid; gap: 0; margin: 16px 0 8px; font-size: 13px; }
.economy-period-totals > div { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px; padding: 10px 0; border-bottom: 1px solid #eef0f4; }
.economy-period-totals dt { color: #64748b; }
.economy-period-totals dd { margin: 0; font-weight: 700; font-variant-numeric: tabular-nums; }
.economy-period-totals .economy-period-result { border: 0; margin-top: 6px; padding: 12px 10px; border-radius: 8px; background: #f8fafc; font-size: 15px; }
@media (max-width: 1200px) { .economy-page .payroll-trend-list { grid-template-columns: repeat(2, minmax(0, 1fr)); } .economy-page .payroll-trend-list > :first-child { grid-column: 1 / -1; } }
@media (max-width: 600px) { .economy-summary, .economy-page .payroll-trend-list { grid-template-columns: minmax(0, 1fr); } .economy-summary-card { padding: 18px; } .economy-page { gap: 24px; } }

.payroll-trend { background: #fff; border-radius: 13px; box-shadow: 0 3px 16px rgba(30, 42, 60, .12); overflow: hidden; color: #3e4653; }
.payroll-trend__header { min-height: 44px; padding: 8px 16px; background: #f7f9ff; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.payroll-trend__year-navigation { margin-left: auto; }
.payroll-trend__header h2 { margin: 0; font-size: 16px; font-weight: 700; }
.payroll-trend__header span { color: #8998b0; font-size: 13px; }
.payroll-trend__body { padding: 12px 16px 10px; }
.payroll-trend__legend { display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 4px; font-size: 14px; font-weight: 700; text-align: center; }
.payroll-trend__legend-mark { width: 22px; height: 3px; background: #68a4df; position: relative; }
.payroll-trend__legend-mark::after { content: ""; width: 9px; height: 9px; border-radius: 50%; background: #68a4df; position: absolute; left: 7px; top: -3px; }
.payroll-trend__scroll { overflow-x: auto; width: 100%; }
.payroll-trend__grid { stroke: #d9dde4; stroke-width: 1; }
.payroll-trend__line { fill: none; stroke: #68a4df; stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; }
.payroll-trend__point { fill: #68a4df; }
.payroll-trend__point--hovered { stroke: #fff; stroke-width: 3; filter: drop-shadow(0 0 4px rgba(70, 142, 210, .8)); }
.payroll-trend__point-hit { fill: transparent; cursor: pointer; }
.payroll-trend__tooltip rect { fill: #344052; filter: drop-shadow(0 3px 6px rgba(20, 30, 45, .22)); }
.payroll-trend__tooltip text { fill: #fff; font-size: 12px; font-weight: 600; }
.payroll-trend__tick, .payroll-trend__axis-title { fill: #5e636c; font-size: 13px; }
.payroll-trend__axis-title { font-weight: 600; }
.payroll-trend__description { margin: 2px 0 0; font-size: 13px; line-height: 1.4; }
.payroll-trend__status { min-height: 180px; display: grid; place-items: center; color: #8998b0; text-align: center; }
@media (max-width: 700px) { .payroll-trend__header { padding: 14px 16px; align-items: flex-start; flex-direction: column; } .payroll-trend__header span { font-size: 13px; } .payroll-trend__body { padding: 22px 16px 18px; } .payroll-trend__description { font-size: 14px; } }
.payroll-trend-list { display: grid; grid-template-columns: minmax(0, 1fr) repeat(2, minmax(220px, 250px)); align-items: stretch; gap: 16px; min-width: 0; max-width: 100%; padding: 8px 0 0; }
.payroll-trend-list > * { min-width: 0; }
.payroll-trend__day-navigation { display: flex; align-items: center; gap: 6px; }
.payroll-trend__day-navigation button { display: grid; place-items: center; flex-shrink: 0; width: 32px; height: 36px; border: 1px solid #d9dde4; border-radius: 6px; background: #fff; color: #3e4653; cursor: pointer; }
.payroll-trend__day-navigation input { min-width: 0; flex: 1; height: 36px; box-sizing: border-box; padding: 4px 6px; border: 1px solid #d9dde4; border-radius: 6px; background: #fff; color: #3e4653; font: inherit; font-size: 13px; }
.payroll-trend__day-navigation button:hover, .payroll-trend__today:hover { background: #f0f6ff; }
.payroll-trend__day-navigation :focus-visible, .payroll-trend__today:focus-visible { outline: 2px solid #3278bc; outline-offset: 2px; }
.payroll-trend__day-result { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; padding: 16px 0 8px; }
.payroll-trend__day-result strong { font-size: 32px; color: #3278bc; line-height: 1; }
.payroll-trend__day-result span, .payroll-trend__day-result p { font-size: 13px; line-height: 1.4; }
.payroll-trend__today { border: 0; border-radius: 4px; background: transparent; color: #3278bc; cursor: pointer; padding: 6px 0; font: inherit; font-size: 13px; }

.payroll-trend__day-note { font-size: 12px; line-height: 1.5; color: #5e636c; margin: 8px 0; }
.payroll-trend--day .payroll-trend__header h2 { font-size: 14px; }
.payroll-trend__day-result strong { overflow-wrap: anywhere; max-width: 100%; }

.payroll-trend--day .payroll-trend__header { padding: 10px 12px; box-sizing: border-box; }
.payroll-trend--day .payroll-trend__body { padding: 12px; }
.payroll-trend--day .payroll-trend__day-result strong { font-size: 28px; }
`);
